import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from '../common/Modal';
import { useApp } from '../../contexts/AppContext';
import { toApiDateTime, toDateInputValue } from '../../utils/dateUtils';

const STATUSES = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'completed', label: 'Completed' },
];

const DEFAULT_CAMPAIGN = {
  name: '',
  description: '',
  status: 'draft',
  selectedSegments: [],
  userId: '',
  creativeUrl: '',
  creativeDuration: '',
  creativeWidth: '',
  creativeHeight: '',
  startDate: '',
  endDate: '',
  frequency: '',
};

const PLAN_DOWNLOAD_STEPS = [
  'Analyzing historical data…',
  'Building the plan…',
  'Preparing the soc-dem info…',
  'Calculating the budget…',
];

const toFormState = (campaign) => {
  if (!campaign) return { ...DEFAULT_CAMPAIGN };

  return {
    ...DEFAULT_CAMPAIGN,
    ...campaign,
    selectedSegments: Array.isArray(campaign.selectedSegments)
      ? campaign.selectedSegments.map(String).filter((id) => id && id !== 'undefined' && id !== 'null')
      : [],
    creativeDuration: campaign.creativeDuration ?? '',
    creativeWidth: campaign.creativeWidth ?? '',
    creativeHeight: campaign.creativeHeight ?? '',
    startDate: toDateInputValue(campaign.startDate),
    endDate: toDateInputValue(campaign.endDate),
  };
};

export default function BuildCampaignModal({ isOpen, onClose, initialCampaign, onSave }) {
  const { segments, getCampaignCost, currentUserType, submitCampaignForModeration, refreshCampaigns, downloadCampaignPlan } = useApp();
  const [form, setForm] = useState(() => toFormState(initialCampaign));
  const [isSaving, setIsSaving] = useState(false);
  const [companyBudget, setCompanyBudget] = useState(null);
  const [companyBudgetLoading, setCompanyBudgetLoading] = useState(false);
  const [isModerateSaving, setIsModerateSaving] = useState(false);
  const [isPlanDownloading, setIsPlanDownloading] = useState(false);

  const [planStepIndex, setPlanStepIndex] = useState(0);
  const planStepIntervalRef = React.useRef(null);



  useEffect(() => {
    if (isOpen) {
      setForm(toFormState(initialCampaign));
      setIsSaving(false);
    }
  }, [initialCampaign, isOpen]);

  const segmentOptions = useMemo(
    () =>
      segments
        .map((segment) => ({
          value: segment.uuid,
          label: segment.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [segments],
  );

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNumberChange = (field, value) => {
    if (value === '') {
      handleFieldChange(field, '');
    } else {
      const numericValue = Number(value);
      if (Number.isNaN(numericValue)) return;
      handleFieldChange(field, numericValue);
    }
  };

  const toggleSegment = (segmentId) => {
    setForm((prev) => {
      const current = new Set(prev.selectedSegments);
      if (current.has(segmentId)) {
        current.delete(segmentId);
      } else {
        current.add(segmentId);
      }
      return {
        ...prev,
        selectedSegments: Array.from(current),
      };
    });
  };

  const handleSubmit = async () => {
    console.log('form', form)
    setIsSaving(true);
    try {
      await onSave({
        ...form,
        selectedSegments: form.selectedSegments,
        creativeDuration: form.creativeDuration === '' ? null : form.creativeDuration,
        creativeWidth: form.creativeWidth === '' ? null : form.creativeWidth,
        creativeHeight: form.creativeHeight === '' ? null : form.creativeHeight,
        startDate: toApiDateTime(form.startDate),
        endDate: toApiDateTime(form.endDate),
        frequency: form.frequency
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleModerate = async () => {
    if (!initialCampaign?.uuid) return;

    setIsModerateSaving(true);
    try {
      await submitCampaignForModeration(initialCampaign.uuid);
      // await refreshCampaigns();
      // await loadCompanyBudget();
      console.log('Submitted for moderation');
    } catch (error) {
      console.error('Failed to submit for moderation', error);
    } finally {
      setIsModerateSaving(false);
    }
  };

  const handleDownloadPlan = async () => {
    if (!initialCampaign?.uuid) return;

    setIsPlanDownloading(true);
    try {
      await downloadCampaignPlan(initialCampaign.uuid, form.name || initialCampaign.name || 'Campaign');
    } catch (error) {
      console.error('Failed to download media plan', error);
      alert('Failed to download media plan');
    } finally {
      setIsPlanDownloading(false);
    }
  };

  const isEditMode = Boolean(initialCampaign?.uuid);
  const isEditStatusMode = currentUserType === 'admin'
  const isModerateSubmitDisabled =
    !initialCampaign?.uuid || form.status !== 'draft' || isModerateSaving;

  const isBudgetVisible = true
  const isEstimatedBudgetDataVisible = form.status === 'draft' || form.status === 'active' || form.status === 'paused'
  const isActualBudgetDataVisible = form.status === 'active' || form.status === 'paused' || form.status === 'completed'

  const loadCompanyBudget = useCallback(async () => {
    if (!initialCampaign?.uuid) {
      setCompanyBudget(null);
      return;
    }

    setCompanyBudgetLoading(true);
    try {
      const cost = await getCampaignCost(initialCampaign.uuid);
      setCompanyBudget(cost);
    } catch (error) {
      console.error('Failed to load campaign cost', error);
      setCompanyBudget(null);
    } finally {
      setCompanyBudgetLoading(false);
    }
  }, [getCampaignCost, initialCampaign?.uuid]);

  useEffect(() => {
    if (!isOpen) return;
    setCompanyBudget(null);
    if (initialCampaign?.uuid) {
      loadCompanyBudget();
    }
  }, [isOpen, initialCampaign?.uuid, loadCompanyBudget]);

  useEffect(() => {
    if (!isPlanDownloading) {
      setPlanStepIndex(0);
      if (planStepIntervalRef.current) {
        clearInterval(planStepIntervalRef.current);
        planStepIntervalRef.current = null;
      }
      return;
    }

    setPlanStepIndex(0);

    planStepIntervalRef.current = setInterval(() => {
      setPlanStepIndex((prev) => {
        const next = prev + 1;

        if (next >= PLAN_DOWNLOAD_STEPS.length - 1) {
          if (planStepIntervalRef.current) {
            clearInterval(planStepIntervalRef.current);
            planStepIntervalRef.current = null;
          }
          return PLAN_DOWNLOAD_STEPS.length - 1;
        }

        return next;
      });
    }, 10_000);

    return () => {
      if (planStepIntervalRef.current) {
        clearInterval(planStepIntervalRef.current);
        planStepIntervalRef.current = null;
      }
    };
  }, [isPlanDownloading]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={isEditMode ? 'Edit Campaign' : 'Create Campaign'}
        size="xlarge"
        footer={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={isSaving}
            >
              {isSaving ? (isEditMode ? 'Saving…' : 'Creating…') : isEditMode ? 'Save Changes' : 'Create Campaign'}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleDownloadPlan}
              disabled={!isEditMode || isPlanDownloading}
            >
              {isPlanDownloading && <span className="spinner spinner--sm" aria-hidden="true" />}
              <span>
                {isPlanDownloading ? PLAN_DOWNLOAD_STEPS[planStepIndex] : 'Download media plan'}
              </span>
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleModerate}
              disabled={isModerateSubmitDisabled}
            >
              {isModerateSaving ? 'Submitting for moderation…' : 'Submit for moderation'}
            </button>
          </>
        }
      >
        <form className="builder-form campaign-builder-form" onSubmit={(event) => event.preventDefault()}>
          <div className="builder-grid two-columns">
            <div className="form-group-modal">
              <label htmlFor="campaign-name">Campaign name</label>
              <input
                id="campaign-name"
                value={form.name}
                onChange={(event) => handleFieldChange('name', event.target.value)}
                placeholder="e.g. Spring Awareness Push"
                required
              />
            </div>
            <div className="form-group-modal">
              <label htmlFor="campaign-status">Status</label>
              <select
                id="campaign-status"
                value={form.status}
                onChange={(event) => handleFieldChange('status', event.target.value)}
                disabled={!isEditStatusMode}
              >
                {STATUSES.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group-modal">
            <label htmlFor="campaign-description">Description</label>
            <textarea
              id="campaign-description"
              value={form.description}
              onChange={(event) => handleFieldChange('description', event.target.value)}
              placeholder="Short description, goals, or context for the campaign…"
              rows={3}
            />
          </div>

          <div className="form-group-modal">
            <label>Target segments</label>
            <div className="segment-picker">
              {segmentOptions.length > 0 ? (
                segmentOptions.map((option) => {
                  const isChecked = form.selectedSegments.includes(option.value);
                  return (
                    <label key={option.value} className={`segment-picker-option ${isChecked ? 'selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleSegment(option.value)}
                      />
                      <span className="segment-picker-label">{option.label}</span>
                    </label>
                  );
                })
              ) : (
                <p className="form-help-text">No segments available yet. Create a segment first to target it.</p>
              )}
            </div>
            {form.selectedSegments.length > 0 && (
              <div className="segment-picker-summary">
                {form.selectedSegments.map((id) => {
                  const segment = segmentOptions.find((opt) => opt.value === id);
                  return (
                    <span key={id} className="segment-chip">
                      {segment?.label ?? `Segment #${id}`}
                      <button type="button" onClick={() => toggleSegment(id)} aria-label="Remove segment">
                        ×
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          <div className="builder-grid three-columns">
            <div className="form-group-modal">
              <label htmlFor="campaign-start-date">Start date</label>
              <input
                id="campaign-start-date"
                type="date"
                value={form.startDate ?? ''}
                onChange={(event) => handleFieldChange('startDate', event.target.value)}
              />
            </div>
            <div className="form-group-modal">
              <label htmlFor="campaign-end-date">End date</label>
              <input
                id="campaign-end-date"
                type="date"
                value={form.endDate ?? ''}
                onChange={(event) => handleFieldChange('endDate', event.target.value)}
              />
            </div>
          </div>

          <div className="builder-subsection">
            <div className="form-group-modal">
              <label htmlFor="campaign-frequency">Frequency</label>
              <input
                id="campaign-frequency"
                value={form.frequency ?? ''}
                onChange={(event) => handleFieldChange('frequency', event.target.value)}
                placeholder="e.g. 5"
                required
              />
            </div>
          </div>

          <div className="builder-subsection">
            <h4>Creative details</h4>
            <p className="subsection-description">
              Provide the media source and basic specifications. These values help validate ad delivery.
            </p>
            <div className="builder-grid four-columns">
              <div className="form-group-modal">
                <label htmlFor="campaign-creative-url">Media URL</label>
                <input
                  id="campaign-creative-url"
                  value={form.creativeUrl ?? ''}
                  onChange={(event) => handleFieldChange('creativeUrl', event.target.value)}
                  placeholder="https://…"
                  required
                />
              </div>
              <div className="form-group-modal">
                <label htmlFor="campaign-creative-duration">Duration (seconds)</label>
                <input
                  id="campaign-creative-duration"
                  type="number"
                  min="0"
                  value={form.creativeDuration ?? ''}
                  onChange={(event) => handleNumberChange('creativeDuration', event.target.value)}
                  placeholder="e.g. 30"
                />
              </div>
              <div className="form-group-modal">
                <label htmlFor="campaign-creative-width">Width (px)</label>
                <input
                  id="campaign-creative-width"
                  type="number"
                  min="0"
                  value={form.creativeWidth ?? ''}
                  onChange={(event) => handleNumberChange('creativeWidth', event.target.value)}
                  placeholder="e.g. 1920"
                />
              </div>
              <div className="form-group-modal">
                <label htmlFor="campaign-creative-height">Height (px)</label>
                <input
                  id="campaign-creative-height"
                  type="number"
                  min="0"
                  value={form.creativeHeight ?? ''}
                  onChange={(event) => handleNumberChange('creativeHeight', event.target.value)}
                  placeholder="e.g. 1080"
                />
              </div>
            </div>
          </div>
        </form>
      </Modal>
      {isOpen && isBudgetVisible && (
        <div className="company-budget-card">
          <div className="company-budget-card-header">
            <span className="company-budget-card-title">Campaign budget</span>
          </div>
          <div className="company-budget-content">
            {companyBudgetLoading ? (
              <div className="company-budget-loader">
                <div className="spinner"></div>
                <span>Calculating...</span>
              </div>
            ) : (
              <div className="company-budget-value-container">
                {isEstimatedBudgetDataVisible && (
                  <>
                    <span className="company-budget-label">Estimated</span>
                    <span className="company-budget-value">
                      {companyBudget?.estimated != null ? companyBudget.estimated.toLocaleString() : '-'}
                    </span>
                  </>
                )}
                {isActualBudgetDataVisible && (
                  <>
                    <span className="company-budget-label">Actual</span>
                    <span className="company-budget-value">
                      {companyBudget?.actual != null ? companyBudget.actual.toLocaleString() : '-'}
                    </span>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

